/*
The MIT License (MIT)

Copyright (c) 2014 Chris Wilson
Copyright (c) 2023 Torgeir Hovden

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import type { StandardNoteNameWithOctave } from '@tunasong/schemas'
import { standardNoteNames } from '@tunasong/schemas'

/** Get the note name, octave and MIDI number of a note  */
export const noteFromFrequency = (frequency: number) => {
  /**
   * Note 69 is A4 @ 440 Hz (i.e.. num = 0)
   * The MIDI range is from A0 (21) to 127 (G9)
   */
  const noteNum = 12 * (Math.log(frequency / 440) / Math.log(2))

  const midiNumber = Math.round(noteNum) + 69
  const octave = Math.round(4 + noteNum / 12)
  const note = standardNoteNames[midiNumber % 12]

  return {
    midiNumber,
    octave,
    note,
  }
}

export const frequencyFromMIDINoteNumber = (midiNoteNumber: number) => 440 * Math.pow(2, (midiNoteNumber - 69) / 12)

export const frequencyFromNoteName = (noteName: StandardNoteNameWithOctave) => {
  // Parse the noteName, on the format of 'A4' or 'A#4'
  const note = noteName.slice(0, -1)
  const octave = parseInt(noteName.slice(-1), 10)

  // The MIDI range is from A0 (21) to 127 (G9)
  // So we calculate from C0 (12)

  return frequencyFromMIDINoteNumber(12 + 12 * octave + standardNoteNames.indexOf(note))
}

export const centsOffFromPitch = (frequency: number, midiNoteNumber: number) =>
  Math.floor((1200 * Math.log(frequency / frequencyFromMIDINoteNumber(midiNoteNumber))) / Math.log(2))
